import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthField, type AuthFieldProps } from './AuthField';

type AuthPasswordFieldProps = Omit<AuthFieldProps, 'type' | 'rightSlot'>;

/** The Caps Lock advisory yields to a real validation error when one is present. */
export const AuthPasswordField = React.forwardRef<HTMLInputElement, AuthPasswordFieldProps>(
  ({ warning, onKeyUp, onKeyDown, onBlur, ...props }, ref) => {
    const { t } = useTranslation();
    const [visible, setVisible] = React.useState(false);
    const [capsLock, setCapsLock] = React.useState(false);

    const syncCapsLock = (event: React.KeyboardEvent<HTMLInputElement>) => {
      // getModifierState is unavailable on synthetic events without a key press;
      // guard so non-key paths (e.g. autofill) don't throw.
      if (typeof event.getModifierState === 'function') {
        setCapsLock(event.getModifierState('CapsLock'));
      }
    };

    return (
      <AuthField
        ref={ref}
        type={visible ? 'text' : 'password'}
        warning={warning ?? (capsLock ? t('auth.caps_lock_on') : undefined)}
        onKeyDown={(event) => {
          syncCapsLock(event);
          onKeyDown?.(event);
        }}
        onKeyUp={(event) => {
          syncCapsLock(event);
          onKeyUp?.(event);
        }}
        onBlur={(event) => {
          setCapsLock(false);
          onBlur?.(event);
        }}
        rightSlot={
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setVisible((prev) => !prev)}
            aria-label={visible ? t('auth.hide_password') : t('auth.show_password')}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
          </button>
        }
        {...props}
      />
    );
  },
);

AuthPasswordField.displayName = 'AuthPasswordField';
